import React, { useState, useRef, useEffect } from "react";

import styled from "@emotion/styled";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Link, useNavigate } from "react-router-dom";
import { Theme } from "../../redux/themeSlice";
import { convertToId } from "../../utils/util";

interface SidebarItemChild {
  label: string;
  url?: string;
  content?: React.ReactNode;
}

export interface SidebarItem {
  id?: number;
  url?: string;
  children?: SidebarItemChild[];
  expanded?: boolean;
  label: string;
  icon?: React.ReactNode;
  handleClick?: Function;
}

interface SidebarListProps {
  sidebarItems: SidebarItem[][];
  children?: React.ReactNode;
  handleClick?: Function;
}

export default function SidebarList({
  sidebarItems,
  children,
  handleClick,
}: SidebarListProps) {
  const sidebarListRef = useRef<HTMLDivElement | null>(null);
  const DEFAULT_EXPANDED_ITEM = -1;
  const SUBSCRIPTIONS_EXPANDED_ITEM = 2;
  const MANAGE_ACCOUNTS_EXPANDED_ITEM = 5;
  const MEASUREMENT_EXPANDED_ITEM = 4;

  const url = location.pathname;
  const navigate = useNavigate();

  let item;
  for (let sidebarItem of sidebarItems) {
    item = sidebarItem.find((item: SidebarItem) => item.expanded);
    if (item) break;
  }

  let current = item ? item.id : DEFAULT_EXPANDED_ITEM;
  const [expandedItem, setExpandedItem] = useState<number>(0);

  function handleExpand(id: number) {
    if (expandedItem === id) setExpandedItem(-1);
    else setExpandedItem(id);
  }

  // desctructure bc the modal will pass its config to the onConfirm function and we want config.path
  function doNavigate(item: SidebarItemChild) {
    navigate(item.url || "");
  }

  const sharedProps = {
    navigate: doNavigate,
    expandedItem,
    handleExpand,
  };

  return (
    <>
      <div className="side-bar-list" ref={sidebarListRef}>
        {sidebarItems.map((group, groupIndex) => (
          <div className="side-bar-section" key={groupIndex}>
            {group.map((item: SidebarItem) => (
              <SidebarListItem
                key={item.id}
                item={{ ...item, id: item.id }}
                {...sharedProps}
              />
            ))}
            {groupIndex !== sidebarItems.length - 1 && <hr className="solid" />}
          </div>
        ))}
        {children}
      </div>
    </>
  );
}

function SidebarListItem({
  item,
  navigate,
  expandedItem,
  handleExpand,
}: {
  item: SidebarItem;
  navigate: Function;
  expandedItem: number;
  handleExpand: Function;
}) {
  const selected = location.pathname == item.url;
  const isExpanded = expandedItem === item.id;
  item.expanded = expandedItem === item.id;
  if (item.id === 3 && item.expanded) item.expanded = true;

  function show() {
    return true;
  }

  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    if (item.children || item.handleClick) {
      event.preventDefault();
      if (item.children) handleExpand(item.id);
      else if (item.handleClick) navigate(item);
    }
  }

  return (
    <div className="parent-nav">
      {item.children ? (
        <>
          <SidebarListItemBody
            selected={selected}
            onClick={handleClick}
            icon={item.icon}
            {...item}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </SidebarListItemBody>
          {isExpanded && (
            <div className="child-nav">
              {item.children.filter(show).map((child) => (
                <SidebarListChildItem
                  key={child.label}
                  item={child}
                  navigate={navigate}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <SidebarListItemBody
          selected={selected}
          onClick={handleClick}
          label={item.label}
          icon={item.icon}
        />
      )}
    </div>
  );
}

function SidebarListChildItem({
  item,
  navigate,
}: {
  item: SidebarItem;
  navigate: Function;
}) {
  const props = {
    onClick(event: React.MouseEvent<HTMLDivElement>) {
      event.preventDefault();
      navigate(item);
    },
    selected: location.pathname == item.url,
    label: item.label,
    icon: item.icon,
    url: item.url,
  };

  return <SidebarListItemBody {...props} />;
}
function SidebarListItemBody({
  label,
  onClick,
  selected,
  icon,
  url = "",
  children = null as React.ReactNode,
  collapsed = false,
  expanded,
  ...rest
}: {
  label: string;
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  selected: boolean;
  icon: React.ReactNode;
  url?: string;
  children?: React.ReactNode;
  collapsed?: boolean;
  expanded?: boolean;
}) {
  const CustomNavLink = styled(
    ({ to, ...props }: { to: string; [key: string]: any }) => (
      <Link to={to} {...props} />
    ),
  )(({ theme }: { theme?: Theme }) => ({
    ["&:hover,&:focus,&:active,&.active,&.selected"]: {
      color: theme ? theme.palette.secondary.main : "Green",
      fontWeight: 600,
    },
  }));

  return (
    <CustomNavLink
      className={`nav-link ${selected && "selected"}`}
      to={url}
      key={"1"}
      onClick={onClick}
      selected={selected}
      expanded={expanded ? "true" : "false"}
      {...rest}
    >
      <span className="nav-icon">{icon}</span>
      {!collapsed && (
        <span
          className="nav-label"
          style={{ fontWeight: selected ? "bold" : 500 }}
        >
          {label}
        </span>
      )}
      {children}
    </CustomNavLink>
  );
}
